import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';

interface LanguageSwitcherProps {
  isHomePage?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ isHomePage = false }) => {
  const { language, setLanguage } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const desktopDropdownRef = useRef<HTMLDivElement>(null);

  const handleLanguageChange = (newLang: 'en' | 'et' | 'ru' | 'fi') => {
    setLanguage(newLang);
    setIsDropdownOpen(false);
    
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(en|ee|ru|fi)/, '');
    const languagePrefix = newLang === 'et' ? 'ee' : newLang;
    const newPath = `/${languagePrefix}${pathWithoutLang || '/'}`;
    navigate(newPath, { replace: true });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedOutsideMobile = mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node);
      const clickedOutsideDesktop = desktopDropdownRef.current && !desktopDropdownRef.current.contains(event.target as Node);
      
      if (clickedOutsideMobile && clickedOutsideDesktop) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'et', label: 'Eesti' },
    { code: 'ru', label: 'Русский' },
    { code: 'fi', label: 'Suomi' }
  ];

  const FlagIcon = ({ lang, className }: { lang: string, className?: string }) => (
    <img 
      src={`/flags/${lang}.svg`} 
      alt={`${lang} flag`}
      className={`object-cover rounded-sm ${className || ''}`} 
    />
  );

  return (
    <div className="flex items-center">
      {/* Mobile version - show current language flag with dropdown */}
      <div className="md:hidden relative" ref={mobileDropdownRef}>
        <button
          className="p-2 flex items-center"
          aria-label="Change language"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <FlagIcon lang={language} className="w-6 h-4" />
        </button>
        <div className={`absolute right-0 mt-1 bg-white shadow-lg rounded-md py-1 z-50 min-w-[80px] ${isDropdownOpen ? 'block' : 'hidden'}`}>
          {languages
            .filter(lang => lang.code !== language)
            .map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code as 'en' | 'et' | 'ru' | 'fi')}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
              >
                <span className="mr-2 flex-shrink-0 flex items-center">
                   <FlagIcon lang={lang.code} className="w-5 h-3.5" />
                </span>
                {lang.code.toUpperCase()}
              </button>
            ))}
        </div>
      </div>
      
      {/* Desktop version with dropdown */}
      <div className="hidden md:block relative" ref={desktopDropdownRef}>
        <button
          className={`flex items-center px-3 py-2 rounded-md text-sm font-medium space-x-2 ${
            isHomePage
              ? 'text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10'
              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
          }`}
          aria-label="Change language"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <FlagIcon lang={language} className="w-5 h-3.5" />
          <span>{language.toUpperCase()}</span>
          <svg
            className={`w-4 h-4 transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div className={`absolute right-0 mt-1 bg-white shadow-lg rounded-md py-1 z-50 min-w-[120px] ${isDropdownOpen ? 'block' : 'hidden'}`}>
          {languages
            .filter(lang => lang.code !== language)
            .map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code as 'en' | 'et' | 'ru' | 'fi')}
                className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full space-x-2"
              >
                <FlagIcon lang={lang.code} className="w-4 h-3" />
                <span>{lang.label}</span>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default LanguageSwitcher;
